import { Link, useParams } from "react-router-dom"
import { app, translate } from "../app"
import TextReader from "./textreader"
import { Container } from "./container"
import { translations } from "../translations"
import { languages } from "../locale"
import { useEffect, useRef, useState } from "react"
import { Footer } from "./footer"
import NotFoundPage from "./notfoundpage"
import { Helmet } from "react-helmet"
import { api } from "../api"
import { generateHreflangTag, getLocaleByZendesk } from "../helpers"
import { Facebook, FacebookMessenger, Telegram, Whatsapp, WhatsappSquare, Envelope, Instagram, Tiktok, ArrowUpOutlined } from "./icons"
import { Breadcrumb } from "../components/breadcrumb"
import { Loader } from "./loader"


// Catch‑all normalizer for messy WYSIWYG content.
function sanitizeArticleHtml(html?: string): string {
  if (!html) return ""
  let out = html
  // Remove legacy align attributes
  out = out.replace(/\salign="[^"]*"/gi, "")
  // De-bloat images: strip fixed sizes and add a helper class
  out = out.replace(/<img/gi, '<img class="article-img"')
  out = out.replace(/\s(width|height)="[^"]*"/gi, "")
  // Convert multiple <br> sequences into paragraph breaks
  out = out.replace(/(?:<br\s*\/?>\s*){2,}/gi, "</p><p>")
  // Remove empty paragraphs (including those with only &nbsp; or <br>)
  out = out.replace(/<p>\s*(?:&nbsp;|\u00A0|\s|<br\s*\/?>)*\s*<\/p>/gi, "")
  // Ensure external links open in a new tab
  out = out.replace(
    /<a\s+([^>]*href="(https?:\/\/[^"]+)")([^>]*)>/gi,
    '<a $1$3 target="_blank" rel="noopener noreferrer">'
  )
  return out
}

const validExtensions = [".pdf", ".doc", ".docx"]

function filterValidAttachments(attachments: ZendeskArticleAttachment[] = []): ZendeskArticleAttachment[] {
  return attachments.filter((attachment) => {
    const lowerCaseFileName = attachment.fileName?.toLowerCase()
    return validExtensions.some((ext) => lowerCaseFileName.endsWith(ext))
  })
}

function getFirstImage(htmlString: string): string | null {
  const imgMatch = htmlString.match(/<img\s[^>]*src="([^"]+)"/i)
  return imgMatch ? imgMatch[1] : null
}

export function Article() {
  let { id, locale } = useParams()
  const [article, setArticle] = useState<ZendeskArticle | null>(null)
  const [articleLoaded, setArticleLoaded] = useState(false)
  const articleId = id.split("-")[0]
  const languageSelected = locale ? getLocaleByZendesk(locale.toLowerCase()) : (localStorage.getItem('preferredLanguage') ? localStorage.getItem('preferredLanguage') : app.locale)
  const selectedLocale = languages[languageSelected]?.zendesk || languageSelected
  const refScrollUp = useRef<HTMLDivElement>(null)
  const [attachments, setAttachments] = useState<ZendeskArticleAttachment[]>([])

  // Removed heavy scroll animations for a calmer load experience

  useEffect(() => {
    if (!articleLoaded || !article) return
    // Smart CMS carries attachments on the article itself; only the Directus
    // path needs a second request for them.
    if (app.siteId) {
      setAttachments(filterValidAttachments(article.attachments || []))
      return
    }
    api.getAttachments(app.country, article.id).then(attachments => {
      setAttachments(filterValidAttachments(attachments || []))
    })
  }, [articleLoaded])

  const isRTL = languages[languageSelected]?.rtl
  const channels = app.page.content.find(x => x.type === 'channels') as BlockChannels
  const category = app.data.zendesk.categories[article?.category] as ZendeskCategory | undefined
  const section = app.data.zendesk.sections[article?.section] as ZendeskSection | undefined



  const metadata = {
    title: article?.name ? translate(article.name) : "",
    body: article?.description ? translate(article.description) : "",
    description: article?.name ? `Explore insights on ${translate(article.name)}` : "",
    image: article?.description ? getFirstImage(translate(article.description)) : null,
    url: `${window.location.origin}/${languageSelected.toLowerCase()}/articles/${articleId}`,
    datePublished: article?.updated_at ? article.updated_at : new Date().toISOString(),
  }
  const sanitizedBody = sanitizeArticleHtml(metadata.body)

  const handleScrollToTop = () => {
    if (refScrollUp.current) refScrollUp?.current?.scrollIntoView({ behavior: "smooth" })
  }

  const hreflangTags = article?.name && generateHreflangTag("articles", article?.name, articleId)

  useEffect(() => {
      const getArticle = async () => {
        setArticleLoaded(false)
        try {
          const fetchedArticle = await app.db.fetchArticle(app.country, +articleId)
          setArticle(fetchedArticle)
        } catch (error) {
          console.log('Error Fetching Article', error)
        }
        setArticleLoaded(true)
      }
    
      getArticle()
    }, [id])

  return <div className={`overflow-y-auto ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>

    <Helmet>
      <link rel="canonical" href={metadata.url} />
    </Helmet>

    <Container className="text-black bg-white" id="main-content">

    {!articleLoaded && <div className="flex items-center justify-center my-16">
        <Loader size={72} width={12} className="bg-gray-500" />
      </div>}

      {articleLoaded && article && (article?.name?.[languageSelected] || !!article?.name) !== undefined && (
        <div className={`flex flex-col ${isRTL ? 'text-right' : ''} fade-in-fast`} style={{ direction: isRTL ? "rtl" : "ltr" }} ref={refScrollUp}>
        {articleLoaded && article && category && section ? (
        <Breadcrumb
        isRtl={isRTL}
        items={[
        { link: `/${selectedLocale.toLowerCase()}`, title: translate(translations.home) },
        { link: `/${selectedLocale.toLowerCase()}/categories/${category.id}`, title: translate(category.name) },
        { link: `/${selectedLocale.toLowerCase()}/categories/${category.id}/${section.id}`, title: translate(section.name) },
        { title: translate(article.name) },
        ]}
        />
        ) : null}
          <div>
            <Helmet>
              {/* General Meta Tags */}
              <title>{metadata.title}</title>
              <meta name="description" content={metadata.description} />
              {hreflangTags?.map((tag, index) => (
                <link key={index} rel="alternate" hrefLang={tag.hreflang} href={tag.href} />
              ))}
              <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_PUBLIC_SITE_URL}/articles/${articleId}`} />

              {/* Open Graph Meta Tags */}
              <meta property="og:title" content={metadata.title} />
              <meta property="og:description" content={metadata.description} />
              <meta property="og:image" content={metadata.image} />
              <meta property="og:url" content={metadata.url} />
              <meta property="og:type" content="article" />
              <meta property="og:locale" content={selectedLocale} />

              {/* Twitter Card Meta Tags */}
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={metadata.title} />
              <meta name="twitter:description" content={metadata.description} />
              <meta name="twitter:image" content={metadata.image} />
            </Helmet>

       
            <h1 className="text-4xl leading-snug">{metadata.title}</h1>
            <p className="text-sm font-light leading-tight">{new Date(article.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
            <div className={`flex text-reader items-center mt-4 relative sm:flex-row flex-col ${isRTL ? 'flex-row-reverse' : ''}`}>
              <TextReader />
            </div>
          </div>

          {article && <div className={`leading-[1.6] text-sm break-words article mt-10 ${isRTL ? 'text-right' : 'text-left'} fade-in-fast`} style={{ direction: isRTL ? "rtl" : "ltr" }} dangerouslySetInnerHTML={{ __html: sanitizedBody }} />}

          {attachments.length > 0 && (
            <div>
              <ul className="list-none pl-5">
                {attachments.map((attachment, index) => (
                  <li key={index} className="mt-4">
                    <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700 hover:no-underline">
                      {attachment.fileName}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="relative w-full mt-8">
            <a onClick={handleScrollToTop} className={`absolute underline cursor-pointer ${isRTL ? 'left-0' : 'right-0'}`}>
              <strong className={`flex items-center text-base font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                {translate(translations.backToTop)}
                <ArrowUpOutlined className={isRTL ? 'mr-2' : 'ml-2'} />
              </strong>
            </a>
          </div>
        </div>
      )}

      {articleLoaded && !article && <NotFoundPage title={translate(translations.oops)} subTitle={translate(translations.pageNotExist)} />}
      {articleLoaded && article && article?.name?.[languageSelected] === undefined && <NotFoundPage title={translate(translations.sorry)} subTitle={translate(translations.pageNotAvailable)} />}

    </Container>

    <div className="py-16 w-full md:flex justify-center text-black bg-[#FFF8F7] h-auto hidden">

      <div className="sm:w-full mx-8 lg:w-4/5 w-screen">

        {channels?.title && <div className="mx-auto text-center text-xl  md:text-2xl font-bold">{translate(channels.title)} </div>}
        {channels?.subtitle && <div className="mx-auto text-center px-4 md:px-6 mt-4 mb-10 text-base md:text-lg">{translate(channels.subtitle)} </div>}

        <div className="flex flex-col md:flex-row flex-wrap gap-2 md:gap-4 justify-center md:justify-between max-w-sm md:max-w-full mx-auto md:p-4">

          {channels?.fb_link && <a href={translate(channels.fb_link)} className="social-icon bg-white" target="_blank" aria-label="Facebook">
            <Facebook className="social-icon-svg" />
            <div className="social-icon-text">{translate(channels.fb)}</div>
          </a>}

          {channels?.fbmess_link && <a href={translate(channels.fbmess_link)} className="social-icon bg-white" target="_blank" aria-label="Facebook Messenger">
            <FacebookMessenger className="social-icon-svg" />
            <div className="social-icon-text">{translate(channels.fbmess)}</div>
          </a>}

          {channels?.whatsapp_link && <a href={translate(channels.whatsapp_link)} className="social-icon bg-white" target="_blank" aria-label="Whatsapp">
            <Whatsapp className="social-icon-svg" />
            <div className="social-icon-text">{translate(channels.whatsapp)}</div>
          </a>}

          {channels?.email_link && <a href={`mailto:${translate(channels.email_link)}`} className="social-icon bg-white" target="_blank" aria-label="Email">
            <Envelope className="social-icon-svg" />
            <div className="social-icon-text">{translate(channels.email)}</div>
          </a>}

          {channels?.instagram_link && <a href={translate(channels.instagram_link)} className="social-icon bg-white" target="_blank" aria-label="Instagram">
            <Instagram className="social-icon-svg" />
            <div className="social-icon-text">{translate(channels.instagram)}</div>
          </a>}

          {channels?.tiktok_link &&
            <a href={translate(channels.tiktok_link)} className="social-icon bg-white" target="_blank" aria-label="Tiktok">
              <Tiktok className="social-icon-svg" />
              <div className="social-icon-text">{translate(channels.toktok)}</div>
            </a>}

          {channels?.telegram_link && <a href={translate(channels.telegram_link)} className="social-icon bg-white" target="_blank" aria-label="Telegram">
            <Telegram className="social-icon-svg" />
            <div className="social-icon-text">{translate(channels.telegram)}</div>
          </a>}

          {channels?.whatsappc_link && <a href={translate(channels.whatsappc_link)} className="social-icon bg-white" target="_blank" aria-label="WhatsApp Channel">
            <WhatsappSquare className="social-icon-svg" />
            <div className="social-icon-text">{translate(channels.whatsappc)}</div>
          </a>}

        </div>
      </div>
    </div>

    <Footer />

  </div>

}